import * as cancerScreeningService from "../../services/cancerScreeningService";
import * as localStorageKeys from "../../constants/localStorageKeys";

import { Grid, useTheme } from "@material-ui/core";
import React, { useContext, useEffect, useState } from "react";
import moment, { Moment } from "moment";

import { CancerScreeningResultModel } from "../../models/cancerScreeningResultModel";
import {
  IFormValues,
} from "../../components/CancerScreening/SentScreeningRequestsFilter";
import { LangContext } from "../../context/LangContext";
import Layout from "../../components/Layout";
import LoadingIndicator from "../../components/LoadingIndicator";
import Section from "../../components/Section";
import SentScreeningRequestsFilter from "../../components/CancerScreening/SentScreeningRequestsFilter";
import SentScreeningRequestsList from "../../components/CancerScreening/SentScreeningRequestsList";
import { TABLE_ROWS_DEFAULT } from "../../constants";
import { clearSavedSentScreeningRequest } from "../../utils";
import { screeningStatuses } from "../../constants/enums";
import translations from "../../assets/json/translations.json";
import { useHistory } from "react-router-dom";

const NewScreeningRequests: React.FC = () => {
  const theme = useTheme();
  const [lang] = useContext(LangContext);


  const [loadingScreeningRequests, setloadingScreeningRequests] =
    useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(TABLE_ROWS_DEFAULT);
  const [count, setCount] = useState(0);
  const [allScreeningRequests, setAllScreeningRequests] = useState<
    CancerScreeningResultModel[] | null
  >(null);
  const [loadForm, setLoadForm] = useState<IFormValues | null>(null);
  const [formCleared, setFormCleared] = useState(false);
  const history = useHistory();

  const initialForm: IFormValues = {
    patientSsn: "",
    requestorSsn: "",
    examinationDateFrom: moment().subtract(6, "months").startOf('day') as Moment | null,
    examinationDateTo: moment().endOf('day') as Moment | null,
    specimenId: "",
    showPendingHPVResult: false,
    showPendingPathologyResult: false,
    showAllResultsReceived: false,
    showHPVPositiveAndNoPathologyRequest: false
  };

  useEffect(() => {
    const form = localStorage.getItem(
      `${localStorageKeys.SENT_SCREENING_REQUESTS_FILTER_FORM}`
    );

    const pageSize = localStorage.getItem(
      `${localStorageKeys.SENT_SCREENING_REQUESTS_PAGE_SIZE}`
    );

    if (pageSize) {
      setRowsPerPage(Number(pageSize));
    }

    if (form) {
      const formJson = JSON.parse(form);

      const formValues: IFormValues = {
        patientSsn: formJson?.patientSsn || "",
        requestorSsn: formJson?.requestorSsn || "",
        examinationDateFrom: formJson?.examinationDateFrom
          ? (moment(formJson.examinationDateFrom) as Moment | null)
          : (null as Moment | null),
        examinationDateTo: formJson?.examinationDateTo
          ? (moment(formJson.examinationDateTo) as Moment | null)
          : (null as Moment | null),
        specimenId: "", // SpecimenId should always start as empty
        showPendingHPVResult: formJson?.showPendingHPVResult,
        showPendingPathologyResult: formJson?.showPendingPathologyResult,
        showAllResultsReceived: formJson?.showAllResultsReceived,
        showHPVPositiveAndNoPathologyRequest: formJson?.showHPVPositiveAndNoPathologyRequest,
      };

      setLoadForm(formValues);
    } else {
      setLoadForm({ ...initialForm });
    }
    /* eslint-disable */
  }, []);
  /* eslint-enable */

  useEffect(() => {
    if (!initialLoad) {
      // save rowsPerPage in localStorage everytime rowsPerPage changes
      localStorage.setItem(
        `${localStorageKeys.SENT_SCREENING_REQUESTS_PAGE_SIZE}`,
        JSON.stringify(rowsPerPage)
      );
    }
    /* eslint-disable */
  }, [rowsPerPage]);
  /* eslint-enable */

  return (
    <Layout useDefaultSpacing title={translations.sentScreeningRequests[lang]}>
      <Section>
        {loadForm && (
          <SentScreeningRequestsFilter
            onSubmit={getNewScreeningRequests}
            onClear={() => {
              setFormCleared(true);
              setAllScreeningRequests(null);
              setCurrentPage(0);
              clearForm();
              clearSavedSentScreeningRequest();
            }}
            loadForm={loadForm || undefined}
            formCleared={formCleared}
          />
        )}

        {loadingScreeningRequests ? (
          <Grid
            container
            justify="center"
            alignItems="center"
            style={{ marginTop: theme.spacing(2) }}
          >
            <LoadingIndicator />
          </Grid>
        ) : (
          allScreeningRequests && (
            <SentScreeningRequestsList
              screeningRequests={allScreeningRequests}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              count={count}
              setCurrentPage={setCurrentPage}
              setRowsPerPage={setRowsPerPage}
            />
          )
        )}
      </Section>
    </Layout>
  );

  async function getNewScreeningRequests(
    values: IFormValues,
    initialLoad?: boolean
  ) {
    setloadingScreeningRequests(true);
    if (!initialLoad) {
      localStorage.setItem(
        `${localStorageKeys.SENT_SCREENING_REQUESTS_FILTER_FORM}`,
        JSON.stringify(values)
      );
    }
    const response = await cancerScreeningService.getCancerScreenings({
      requestorSsn: values?.requestorSsn,
      requestorName: "",
      personSsn: values?.patientSsn,
      personName: "",
      dateTimeFrom: values?.examinationDateFrom
        ? new Date(values?.examinationDateFrom.toISOString())
        : undefined,
      dateTimeTo: values?.examinationDateTo
        ? new Date(values.examinationDateTo.toISOString())
        : undefined,
      specimenId: values.specimenId,
      screeningStatus: [
        screeningStatuses.REQUEST_SENT,
        screeningStatuses.SOME_RESULTS_ARRIVED,
        screeningStatuses.ALL_RESULTS_ARRIVED,
      ],
    });

    if (response.isOk) {
      let cancerScreeningRequests = response?.data?.cancerScreenings;

      if (cancerScreeningRequests?.length === 1 && (values.specimenId.length === 11 || values.specimenId.length === 12)) {
        // Automatically open this screening request
        history.push(`/sent-screening-request/${cancerScreeningRequests[0].id}`)
        return;
      }
      
      if (
        cancerScreeningRequests !== null &&
        (values.showPendingHPVResult || values.showPendingPathologyResult || values.showAllResultsReceived || values.showHPVPositiveAndNoPathologyRequest)
      ) {
        cancerScreeningRequests = filterCancerScreeningRequests(cancerScreeningRequests, values)
      }

      setAllScreeningRequests(cancerScreeningRequests || []);

      setCount(cancerScreeningRequests?.length || 0);
      setloadingScreeningRequests(false);
      setFormCleared(false);
      setCurrentPage(0);
      if (initialLoad) {
        setInitialLoad(false);
        setLoadForm(values);
      }
    } else {
      setloadingScreeningRequests(false);
      setAllScreeningRequests(null);
    }
  }

  function filterCancerScreeningRequests(cancerScreenings: CancerScreeningResultModel[] | undefined, values: IFormValues): CancerScreeningResultModel[] | undefined {
    const filteredOutScreeningRequests: { [id: string]: boolean } = {};

    cancerScreenings?.forEach((c) => {
      if (c.id) {
        if (values.showPendingHPVResult) {
          // Show screening requests that have been sent to hpv test but are still waiting for the result
          if (!(c.hpvTestCreateDate != null && c.hpvTestConclusionDate == null)) {
            // Filter this screening request out
            filteredOutScreeningRequests[c.id] = true;
          }
        }

        if (values.showPendingPathologyResult) {
          // Show screening requests that have been sent to pathology test but are still waiting for the result
          if (!(c.pathologyTestCreateDate != null && c.pathologyTestConclusionDate == null)) {
            // Filter this screening request out
            filteredOutScreeningRequests[c.id] = true;
          }
        }

        if (values.showAllResultsReceived) {
          // Show screening requests that have gotten results from all tests that they were sent to
          if ((c.hpvTestCreateDate != null && c.hpvTestConclusionDate == null)
            || (c.pathologyTestCreateDate != null && c.pathologyTestConclusionDate == null)) {
            filteredOutScreeningRequests[c.id] = true;
          }
        }

        if (values.showHPVPositiveAndNoPathologyRequest) {
          // Show screening requests that have a postive HPV code and a pathology request hasn't been sent
          if (!(c.positiveHPVResultCode && c.pathologyTestCreateDate == null)) {
            filteredOutScreeningRequests[c.id] = true;
          }
        }
      }
    });

    return cancerScreenings?.filter(
      (c) => c.id && !filteredOutScreeningRequests[c.id]
    );
  }

  function clearForm() {
    setLoadForm(initialForm);
  }
};

export default NewScreeningRequests;
